import { useRef, useState } from 'react';
import { dateToISODate } from '@amzn/storm-ui-date-picker';
import { ArtEventInterface } from '../interfaces/editArtEventInterfaces';
import { useUpdateArtEventMutation } from './useUpdateArtEventMutation';
import { DEFAULT_ART_EVENT_WAITLIST } from '../constants/artEventWaitlist';

export const useEditArtEventForm = (
  artEvent: ArtEventInterface,
  onClose: () => void,
  onSuccess: (message: string) => void,
  onError?: (message: string) => void
) => {
  const [startDate, setStartDate] = useState<Date>(
    new Date(artEvent.startTime)
  );
  const [endDate, setEndDate] = useState<Date>(new Date(artEvent.endTime));
  const [activeStartDatePicker, setActiveStartDatePicker] =
    useState<boolean>(false);
  const [activeEndDatePicker, setActiveEndDatePicker] =
    useState<boolean>(false);

  // The waitlist is a one-way transition (false -> true). Once the Art Event already has it enabled,
  // the toggle is locked on; otherwise the user may opt in to the waitlist.
  const initialWaitlist = artEvent.waitlistEnabled ?? DEFAULT_ART_EVENT_WAITLIST;
  const isWaitlistLocked = initialWaitlist;
  const [waitlistEnabled, setWaitlistEnabled] = useState<boolean>(initialWaitlist);

  const handleWaitlistToggle = () => {
    // Cannot disable the waitlist once enabled.
    if (isWaitlistLocked) {
      return;
    }
    setWaitlistEnabled((prev) => !prev);
  };
  const [validationErrors] = useState<{
    startDate?: string;
    endDate?: string;
  }>({});

  const anchorRefStart = useRef<HTMLInputElement>(null);
  const anchorRefEnd = useRef<HTMLInputElement>(null);

  const updateMutation = useUpdateArtEventMutation();

  const selectedStart = startDate ? dateToISODate(startDate) : '';
  const selectedEnd = endDate ? dateToISODate(endDate) : '';

  const handleDateSelect = (date: Date, isStartDate: boolean) => {
    if (isStartDate) {
      setStartDate(date);
      setActiveStartDatePicker(false);
      if (date > endDate) {
        setEndDate(date);
      }
    } else {
      setEndDate(date);
      setActiveEndDatePicker(false);
    }
  };

  const handleSubmit = () => {
    const apiRequestData = {
      artEvent: {
        artEventID: artEvent.artEventID,
        startTimestamp: startDate.getTime(),
        endTimestamp: endDate.getTime(),
        status: artEvent.status,
        // Only send the flag when the user actually changed it; otherwise omit so the backend
        // treats the update as "waitlist unchanged" and preserves the persisted value.
        waitlistEnabled: waitlistEnabled === initialWaitlist ? undefined : waitlistEnabled,
      },
    };

    updateMutation.mutate(apiRequestData, {
      onSuccess: () => {
        onSuccess('art-event-updated-successfully');
        onClose();
      },
      onError: (error: Error) => {
        // Don't report success on a rejected update (e.g. the waitlist was enabled by another user
        // and the backend rejected a backward transition). Surface the error and close.
        onError?.(error?.message || 'art-event-update-failed');
        onClose();
      },
    });
  };

  const handleCancel = () => {
    onClose();
  };

  return {
    dateProps: {
      startDate,
      endDate,
      activeStartDatePicker,
      activeEndDatePicker,
      selectedStart,
      selectedEnd,
      anchorRefStart,
      anchorRefEnd,
      setActiveStartDatePicker,
      setActiveEndDatePicker,
      handleDateSelect,
    },
    buttonProps: {
      handleSubmit,
      handleCancel,
    },
    waitlistProps: {
      waitlistEnabled,
      isWaitlistLocked,
      handleWaitlistToggle,
    },
    validationErrors,
  };
};
